// Self-contained AI-blog reader (index + article). Kept OUT of the giant
// App.jsx — App only detects the /blog[/slug] route and renders <BlogPage/>.
import React, { useState, useEffect, useCallback } from "react";
import { fetchBlogPosts, fetchBlogPost, fetchRelatedPosts, fetchBlogAlternate, blogLocaleFor, BLOG_CATEGORIES } from "../lib/blog";

const wrap = { maxWidth: 760, margin: "0 auto", padding: "0 18px" };
const catLabel = (key, en) => (BLOG_CATEGORIES[key] ? (en ? BLOG_CATEGORIES[key].en : BLOG_CATEGORIES[key].zh) : key);

// Typography for the article HTML body (injected via dangerouslySetInnerHTML,
// so it must be styled with CSS, not React inline styles). Large, clearly
// separated subtitles fix the "wall of text" look.
const BLOG_CSS = `
.blog-body{font-size:17px;line-height:1.95;color:#454562;font-weight:500;}
.blog-body h2{font-size:24px;font-weight:900;color:#2A2A44;margin:36px 0 12px;line-height:1.3;padding-bottom:9px;border-bottom:2px solid var(--c-light,#FCE7EF);}
.blog-body h2:first-child{margin-top:6px;}
.blog-body h3{font-size:19px;font-weight:800;color:#3A3A56;margin:28px 0 8px;line-height:1.4;}
.blog-body p{margin:0 0 18px;}
.blog-body ul,.blog-body ol{margin:0 0 18px;padding-left:22px;}
.blog-body li{margin-bottom:9px;line-height:1.8;}
.blog-body strong{color:#2A2A44;font-weight:800;}
.blog-body blockquote{margin:0 0 18px;padding:12px 18px;border-left:4px solid var(--c-primary,#FF6B8B);background:var(--c-lighter,#FFF3F7);border-radius:0 12px 12px 0;color:#5A5A78;font-style:italic;}
.blog-body a{color:var(--c-primary,#FF6B8B);font-weight:700;}
`;

function BlogHeader({ en }) {
  return (
    <header style={{ position: "sticky", top: 0, zIndex: 50, background: "rgba(255,255,255,0.92)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", borderBottom: "1px solid var(--c-light,#FCE7EF)" }}>
      <div style={{ maxWidth: 960, margin: "0 auto", padding: "11px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <a href="/" style={{ display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none", minWidth: 0 }}>
          <span style={{ fontSize: 22, lineHeight: 1 }}>🐾</span>
          <span style={{ fontWeight: 900, fontSize: 18, color: "var(--c-primary,#FF6B8B)", letterSpacing: 0.2 }}>PettoBook</span>
          <span style={{ fontWeight: 800, fontSize: 12.5, color: "#AFAFC4", borderLeft: "1px solid #ECECF3", paddingLeft: 9, whiteSpace: "nowrap" }}>{en ? "Blog" : "美容知識庫"}</span>
        </a>
        <a href="/#request-demo" style={{ background: "var(--c-primary,#FF6B8B)", color: "#fff", fontWeight: 800, fontSize: 12.5, padding: "7px 15px", borderRadius: 99, textDecoration: "none", whiteSpace: "nowrap" }}>{en ? "Try free" : "免費試用"}</a>
      </div>
    </header>
  );
}

function Shell({ en, children }) {
  return (
    <div style={{ background: "#FCFCFF", minHeight: "100vh", paddingBottom: 60 }}>
      <style dangerouslySetInnerHTML={{ __html: BLOG_CSS }} />
      <BlogHeader en={en} />
      {children}
    </div>
  );
}

function Cover({ url, title, h = 200, radius = 16 }) {
  if (!url) {
    return <div style={{ height: h, borderRadius: radius, background: "linear-gradient(135deg,var(--c-light),var(--c-grad2,#FFD8E8))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40 }}>🐾</div>;
  }
  return <img src={url} alt={title} loading="lazy" style={{ width: "100%", height: h, objectFit: "cover", borderRadius: radius, display: "block" }} />;
}

function CtaBox({ en }) {
  return (
    <div style={{ background: "linear-gradient(135deg,var(--c-primary,#FF6B8B),var(--c-grad2,#FF8FB0))", borderRadius: 20, padding: "22px 20px", color: "#fff", textAlign: "center", margin: "28px 0" }}>
      <p style={{ fontWeight: 900, fontSize: 18, margin: "0 0 6px" }}>{en ? "Run your grooming salon the easy way" : "輕鬆經營你的寵物美容店"}</p>
      <p style={{ fontSize: 13, opacity: 0.95, margin: "0 0 14px", fontWeight: 700 }}>{en ? "Online booking, scheduling, CRM, loyalty & more — free to start." : "網上預約、排班、客戶管理、會員積分一站搞掂 — 免費開始。"}</p>
      <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
        <a href="/#request-demo" style={{ background: "#fff", color: "var(--c-primary,#FF6B8B)", fontWeight: 900, fontSize: 14, padding: "11px 20px", borderRadius: 99, textDecoration: "none" }}>{en ? "Get a demo account" : "領取示範帳戶"}</a>
        <a href="/" style={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 900, fontSize: 14, padding: "11px 20px", borderRadius: 99, textDecoration: "none", border: "1.5px solid rgba(255,255,255,0.6)" }}>{en ? "Open a free shop" : "免費開店"}</a>
      </div>
    </div>
  );
}

function ShareButton({ title, en }) {
  const [copied, setCopied] = useState(false);
  const onShare = useCallback(async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    try {
      if (navigator.share) { await navigator.share({ title, url }); return; }
      await navigator.clipboard.writeText(url);
      setCopied(true); setTimeout(() => setCopied(false), 1800);
    } catch { /* user cancelled */ }
  }, [title]);
  return (
    <button onClick={onShare} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "var(--c-lighter,#FFF3F7)", color: "var(--c-primary,#FF6B8B)", border: "1.5px solid var(--c-border,#F1D6E0)", borderRadius: 99, padding: "8px 16px", fontWeight: 800, fontSize: 13, cursor: "pointer" }}>
      🔗 {copied ? (en ? "Link copied!" : "已複製連結") : (en ? "Share" : "分享")}
    </button>
  );
}

function Card({ p, en, onOpen }) {
  return (
    <a href={"/blog/" + p.slug} onClick={(e) => { if (onOpen) { e.preventDefault(); onOpen(p.slug); } }}
       style={{ display: "block", textDecoration: "none", color: "inherit", background: "#fff", borderRadius: 18, overflow: "hidden", border: "1px solid var(--c-light,#FCE7EF)", boxShadow: "0 2px 10px rgba(0,0,0,0.04)" }}>
      <Cover url={p.cover_url} title={p.title} h={150} radius={0} />
      <div style={{ padding: "12px 14px 16px" }}>
        {p.category && <span style={{ fontSize: 11, fontWeight: 800, color: "var(--c-primary,#FF6B8B)" }}>{catLabel(p.category, en)}</span>}
        <p style={{ fontWeight: 900, fontSize: 15, margin: "4px 0 4px", color: "#3A3A56", lineHeight: 1.35 }}>{p.title}</p>
        {p.description && <p style={{ fontSize: 12.5, color: "#8A8A9E", fontWeight: 600, margin: 0, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{p.description}</p>}
      </div>
    </a>
  );
}

export default function BlogPage({ mode = "index", slug = null, appLocale = "zh-HK", onOpenPost, onHome }) {
  const [posts, setPosts] = useState(null);
  const [post, setPost] = useState(undefined); // undefined = loading, null = not found
  const [related, setRelated] = useState([]);
  const [altSlug, setAltSlug] = useState(null);
  const [catFilter, setCatFilter] = useState(null); // null = all categories
  const [query, setQuery] = useState("");
  const indexLocale = blogLocaleFor(appLocale);

  useEffect(() => {
    let dead = false;
    if (mode === "index") {
      setPosts(null);
      fetchBlogPosts({ locale: indexLocale, limit: 200 }).then(r => { if (!dead) setPosts(r); }).catch(() => { if (!dead) setPosts([]); });
    } else if (mode === "post" && slug) {
      setPost(undefined); setRelated([]); setAltSlug(null);
      fetchBlogPost(slug).then(async (p) => {
        if (dead) return;
        setPost(p);
        if (p) {
          if (typeof document !== "undefined") document.title = p.title + " | PettoBook";
          const other = p.locale !== "zh-HK" ? "zh-HK" : "en";
          fetchBlogAlternate(p.group_id, other).then(a => { if (!dead) setAltSlug(a?.slug || null); });
          fetchRelatedPosts({ tags: p.tags || [], excludeId: p.id, locale: p.locale, limit: 4 }).then(r => { if (!dead) setRelated(r); });
        }
      }).catch(() => { if (!dead) setPost(null); });
    }
    return () => { dead = true; };
  }, [mode, slug, indexLocale]);

  const goHome = () => { if (onHome) onHome(); else window.location.href = "/"; };
  const openPost = (s) => { if (onOpenPost) onOpenPost(s); else window.location.href = "/blog/" + s; };

  // ── INDEX ───────────────────────────────────────────────────────────────
  if (mode === "index") {
    const en = indexLocale === "en";
    const all = posts || [];
    // Categories that actually have posts, in the canonical order.
    const presentCats = Object.keys(BLOG_CATEGORIES).filter(k => all.some(p => p.category === k));
    const ql = query.trim().toLowerCase();
    const filtered = all.filter(p =>
      (!catFilter || p.category === catFilter) &&
      (!ql || (p.title || "").toLowerCase().includes(ql) || (p.description || "").toLowerCase().includes(ql) || (p.tags || []).some(t => String(t).toLowerCase().includes(ql)))
    );
    const chip = (active) => ({ fontSize: 13, fontWeight: 800, padding: "7px 14px", borderRadius: 99, cursor: "pointer", whiteSpace: "nowrap", border: "1.5px solid " + (active ? "var(--c-primary,#FF6B8B)" : "var(--c-border,#F1D6E0)"), background: active ? "var(--c-primary,#FF6B8B)" : "#fff", color: active ? "#fff" : "#6A6A85" });
    return (
      <Shell en={en}>
        {/* Hero band */}
        <div style={{ background: "linear-gradient(135deg,var(--c-lighter,#FFF3F7),#FCFCFF)", borderBottom: "1px solid var(--c-light,#FCE7EF)" }}>
          <div style={{ maxWidth: 960, margin: "0 auto", padding: "34px 18px 30px" }}>
            <p style={{ fontWeight: 800, fontSize: 13, color: "var(--c-primary,#FF6B8B)", margin: "0 0 8px", letterSpacing: 0.5 }}>{en ? "PETTOBOOK KNOWLEDGE HUB" : "PETTOBOOK 美容知識庫"}</p>
            <h1 style={{ fontWeight: 900, fontSize: 34, color: "#2A2A44", margin: "0 0 10px", lineHeight: 1.2 }}>{en ? "Grow your grooming business" : "經營更好的寵物美容店"}</h1>
            <p style={{ color: "#7A7A92", fontWeight: 600, fontSize: 16, margin: 0, lineHeight: 1.6, maxWidth: 620 }}>{en ? "Practical guides on grooming skills, styling, dog behaviour, hiring, and how to run a salon — updated daily." : "每日更新：美容技巧、造型設計、狗狗行為、招聘面試、開店與店舖營運實戰教學。"}</p>
          </div>
        </div>
        <div style={{ maxWidth: 960, margin: "0 auto", padding: "22px 18px 0" }}>
          {/* Search + category filter */}
          {all.length > 0 && (
            <div style={{ margin: "0 0 20px" }}>
              <div style={{ position: "relative", marginBottom: 12 }}>
                <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 15 }}>🔍</span>
                <input
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={en ? "Search articles…" : "搜尋文章…"}
                  style={{ width: "100%", boxSizing: "border-box", padding: "12px 14px 12px 40px", borderRadius: 14, border: "1.5px solid var(--c-border,#F1D6E0)", fontSize: 15, fontWeight: 600, outline: "none", background: "#fff", color: "#3A3A56" }}
                />
              </div>
              <div style={{ display: "flex", gap: 8, overflowX: "auto", WebkitOverflowScrolling: "touch", paddingBottom: 4 }}>
                <button onClick={() => setCatFilter(null)} style={chip(!catFilter)}>{en ? "All" : "全部"}</button>
                {presentCats.map(k => (
                  <button key={k} onClick={() => setCatFilter(catFilter === k ? null : k)} style={chip(catFilter === k)}>{catLabel(k, en)}</button>
                ))}
              </div>
            </div>
          )}
          {posts === null
            ? <p style={{ color: "#AFAFC4", fontWeight: 700 }}>{en ? "Loading…" : "載入中…"}</p>
            : all.length === 0
              ? <p style={{ color: "#AFAFC4", fontWeight: 700 }}>{en ? "Articles coming soon." : "文章即將推出。"}</p>
              : filtered.length === 0
                ? <p style={{ color: "#AFAFC4", fontWeight: 700, padding: "10px 0" }}>{en ? "No articles match your search." : "沒有符合的文章。"}</p>
                : <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 18 }}>
                    {filtered.map(p => <Card key={p.slug} p={p} en={en} onOpen={openPost} />)}
                  </div>}
          <CtaBox en={en} />
        </div>
      </Shell>
    );
  }

  // ── ARTICLE ─────────────────────────────────────────────────────────────
  if (post === undefined) return <Shell en={false}><div style={{ ...wrap, paddingTop: 60, color: "#AFAFC4", fontWeight: 700 }}>載入中…</div></Shell>;
  if (post === null) {
    return (
      <Shell en={false}>
        <div style={{ ...wrap, paddingTop: 60, textAlign: "center" }}>
          <p style={{ fontWeight: 800, color: "#3A3A56" }}>找不到此文章 / Article not found</p>
          <a href="/blog" style={{ color: "var(--c-primary,#FF6B8B)", fontWeight: 800 }}>← 返回知識庫</a>
        </div>
      </Shell>
    );
  }
  const en = post.locale === "en";
  const dateStr = post.published_at ? String(post.published_at).slice(0, 10) : "";
  return (
    <Shell en={en}>
      <article style={{ ...wrap, paddingTop: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <a href="/blog" style={{ color: "var(--c-primary,#FF6B8B)", fontWeight: 800, fontSize: 13, textDecoration: "none" }}>← {en ? "Knowledge Hub" : "美容知識庫"}</a>
          {altSlug && <a href={"/blog/" + altSlug} style={{ fontSize: 12, fontWeight: 800, color: "#8A8A9E", textDecoration: "none", border: "1px solid var(--c-border,#F1D6E0)", borderRadius: 99, padding: "5px 12px" }}>{en ? "🇭🇰 中文" : "🇬🇧 English"}</a>}
        </div>
        {post.category && <div style={{ margin: "16px 0 6px" }}><span style={{ fontSize: 12.5, fontWeight: 800, color: "#fff", background: "var(--c-primary,#FF6B8B)", borderRadius: 99, padding: "4px 12px" }}>{catLabel(post.category, en)}</span></div>}
        <h1 style={{ fontWeight: 900, fontSize: 33, color: "#22223C", margin: "4px 0 10px", lineHeight: 1.22 }}>{post.title}</h1>
        {post.description && <p style={{ fontSize: 17, color: "#7A7A92", fontWeight: 600, lineHeight: 1.6, margin: "0 0 16px" }}>{post.description}</p>}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, margin: "0 0 18px", paddingBottom: 16, borderBottom: "1px solid var(--c-light,#FCE7EF)" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, color: "#8A8A9E", fontWeight: 800 }}><span style={{ fontSize: 16 }}>🐾</span> PettoBook 編輯部{dateStr ? " · " + dateStr : ""}</span>
          <ShareButton title={post.title} en={en} />
        </div>
        <Cover url={post.cover_url} title={post.title} h={300} />
        <div className="blog-body" style={{ marginTop: 26 }} dangerouslySetInnerHTML={{ __html: post.body || "" }} />
        {(post.tags || []).length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "26px 0 0" }}>
            {post.tags.map(t => <span key={t} style={{ fontSize: 12.5, fontWeight: 800, color: "var(--c-primary,#FF6B8B)", background: "var(--c-lighter,#FFF3F7)", borderRadius: 99, padding: "6px 13px" }}>#{t}</span>)}
          </div>
        )}
        <CtaBox en={en} />
        {related.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <h2 style={{ fontWeight: 900, fontSize: 20, color: "#3A3A56", margin: "0 0 16px" }}>{en ? "Related articles" : "相關文章"}</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16 }}>
              {related.map(p => <Card key={p.slug} p={p} en={en} onOpen={openPost} />)}
            </div>
          </div>
        )}
      </article>
    </Shell>
  );
}
